"use client";

import * as React from "react";

import { IconLoader } from "./AuthIcons";

// =============================================================================
// Heading
// =============================================================================

export function AuthHeading({
  title,
  subtitle,
  align = "left",
}: {
  readonly title: React.ReactNode;
  readonly subtitle?: React.ReactNode;
  readonly align?: "left" | "center";
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        textAlign: align,
      }}
    >
      <h1
        style={{
          margin: 0,
          fontSize: 30,
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          fontWeight: 600,
          color: "var(--auth-text)",
        }}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          style={{
            margin: 0,
            fontSize: 14.5,
            lineHeight: 1.55,
            color: "var(--auth-text-muted)",
            maxWidth: 440,
            ...(align === "center" ? { marginLeft: "auto", marginRight: "auto" } : {}),
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Field (label + control + error/hint)
// =============================================================================

export function AuthField({
  label,
  error,
  hint,
  children,
  htmlFor,
}: {
  readonly label?: React.ReactNode;
  readonly error?: React.ReactNode;
  readonly hint?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly htmlFor?: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <label
          htmlFor={htmlFor}
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            color: "var(--auth-text-muted)",
            letterSpacing: "-0.005em",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {typeof label === "string" ? <span>{label}</span> : label}
        </label>
      )}
      {children}
      {(error || hint) && (
        <span
          style={{
            fontSize: 12,
            color: error ? "var(--auth-danger)" : "var(--auth-text-subtle)",
            minHeight: 16,
          }}
        >
          {error || hint}
        </span>
      )}
    </div>
  );
}

// =============================================================================
// TextInput (icon-prefixed, focus ring, optional suffix)
// =============================================================================

type AuthInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  readonly icon?: React.ReactNode;
  readonly suffix?: React.ReactNode;
  readonly hasError?: boolean;
};

export const AuthInput = React.forwardRef<HTMLInputElement, AuthInputProps>(
  function AuthInput({ icon, suffix, hasError, style, onFocus, onBlur, ...rest }, ref) {
    const [focus, setFocus] = React.useState(false);
    const ringColor = hasError
      ? "var(--auth-danger)"
      : focus
      ? "var(--auth-primary)"
      : "transparent";
    const borderColor = hasError
      ? "var(--auth-danger)"
      : focus
      ? "var(--auth-primary)"
      : "var(--auth-border-strong)";

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: "var(--auth-surface)",
          border: `1px solid ${borderColor}`,
          borderRadius: 8,
          height: 40,
          padding: "0 12px",
          gap: 8,
          transition: "border-color 0.12s, box-shadow 0.12s",
          boxShadow: focus
            ? `0 0 0 3px color-mix(in oklab, ${ringColor} 18%, transparent)`
            : "none",
        }}
      >
        {icon && (
          <span
            style={{
              color: "var(--auth-text-subtle)",
              display: "inline-flex",
            }}
          >
            {icon}
          </span>
        )}
        <input
          ref={ref}
          {...rest}
          onFocus={(e) => {
            setFocus(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocus(false);
            onBlur?.(e);
          }}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            color: "var(--auth-text)",
            fontSize: 14,
            padding: 0,
            minWidth: 0,
            ...style,
          }}
        />
        {suffix}
      </div>
    );
  },
);

// =============================================================================
// Primary button (matches design's primary CTA)
// =============================================================================

export function AuthButton({
  children,
  loading,
  disabled,
  iconRight,
  variant = "primary",
  type = "button",
  onClick,
  style,
}: {
  readonly children: React.ReactNode;
  readonly loading?: boolean;
  readonly disabled?: boolean;
  readonly iconRight?: React.ReactNode;
  readonly variant?: "primary" | "secondary";
  readonly type?: "button" | "submit" | "reset";
  readonly onClick?: () => void;
  readonly style?: React.CSSProperties;
}) {
  const isDisabled = disabled || loading;
  const variantStyle: React.CSSProperties =
    variant === "primary"
      ? {
          background: "var(--auth-primary)",
          color: "#fff",
          border: "1px solid color-mix(in oklab, var(--auth-primary) 80%, black)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(15,17,26,0.08)",
        }
      : {
          background: "var(--auth-surface)",
          color: "var(--auth-text)",
          border: "1px solid var(--auth-border-strong)",
          boxShadow: "var(--auth-shadow-sm)",
        };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "0 18px",
        height: 44,
        fontSize: 14.5,
        fontWeight: 500,
        letterSpacing: "-0.005em",
        borderRadius: 8,
        opacity: isDisabled ? 0.55 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
        transition: "filter 0.12s ease, background 0.12s ease",
        width: "100%",
        ...variantStyle,
        ...style,
      }}
    >
      {loading ? <IconLoader size={16} /> : null}
      {children}
      {!loading && iconRight}
    </button>
  );
}

// =============================================================================
// Divider with label
// =============================================================================

export function AuthDivider({ children }: { readonly children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        color: "var(--auth-text-subtle)",
        fontSize: 12,
      }}
    >
      <div style={{ flex: 1, height: 1, background: "var(--auth-border)" }} />
      <span style={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {children}
      </span>
      <div style={{ flex: 1, height: 1, background: "var(--auth-border)" }} />
    </div>
  );
}

// =============================================================================
// Password strength meter
// =============================================================================

const STRENGTH_COLORS = [
  "#e5484d",
  "#e5484d",
  "#f5a524",
  "#3b7bf6",
  "#2bb673",
  "#2bb673",
];
const STRENGTH_LABELS = ["Too weak", "Weak", "Okay", "Good", "Strong", "Excellent"];

export function passwordStrength(pw: string): {
  score: number;
  label: string;
  tips: string[];
} {
  if (!pw) return { score: 0, label: "", tips: [] };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  const tips: string[] = [];
  if (pw.length < 8) tips.push("At least 8 characters");
  if (!/\d/.test(pw)) tips.push("a number");
  if (!/[^A-Za-z0-9]/.test(pw)) tips.push("a symbol");

  return { score, label: STRENGTH_LABELS[score], tips };
}

// =============================================================================
// OTP Input — 6 single-character boxes with auto-advance and paste support
// =============================================================================

export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled,
  hasError,
  autoFocus = true,
}: {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly length?: number;
  readonly disabled?: boolean;
  readonly hasError?: boolean;
  readonly autoFocus?: boolean;
}) {
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);
  const digits = React.useMemo(() => {
    const padded = value.padEnd(length, " ").slice(0, length);
    return Array.from(padded);
  }, [value, length]);

  React.useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const setDigit = (index: number, char: string) => {
    const clean = char.replace(/\D/g, "").slice(0, 1);
    const next = [...digits];
    next[index] = clean || " ";
    onChange(next.join("").replace(/\s+$/g, "").trim().slice(0, length));
    if (clean) {
      const target = refs.current[Math.min(index + 1, length - 1)];
      target?.focus();
      target?.select();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      const current = digits[index]?.trim();
      if (current) {
        // Clear current box, stay put.
        const next = [...digits];
        next[index] = " ";
        onChange(next.join("").replace(/\s+$/g, "").trim());
      } else if (index > 0) {
        // Hop back and clear there.
        const next = [...digits];
        next[index - 1] = " ";
        onChange(next.join("").replace(/\s+$/g, "").trim());
        refs.current[index - 1]?.focus();
      }
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowLeft" && index > 0) {
      refs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      refs.current[index + 1]?.focus();
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    const target = refs.current[Math.min(text.length, length - 1)];
    target?.focus();
    target?.select();
  };

  const borderColor = hasError
    ? "var(--auth-danger)"
    : "var(--auth-border-strong)";

  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={d.trim()}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.currentTarget.select()}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          aria-label={`Digit ${i + 1} of ${length}`}
          style={{
            flex: 1,
            minWidth: 0,
            height: 52,
            textAlign: "center",
            fontSize: 22,
            fontWeight: 500,
            letterSpacing: "-0.01em",
            color: "var(--auth-text)",
            background: "var(--auth-surface)",
            border: `1px solid ${borderColor}`,
            borderRadius: 10,
            outline: "none",
            transition: "border-color 0.12s, box-shadow 0.12s",
          }}
        />
      ))}
    </div>
  );
}

export function PasswordStrengthMeter({ value }: { readonly value: string }) {
  const s = passwordStrength(value);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 2 }}>
      <div style={{ display: "flex", gap: 4 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 3,
              background:
                i < s.score ? STRENGTH_COLORS[s.score] : "var(--auth-border)",
              transition: "background 0.2s",
            }}
          />
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11.5,
          color: "var(--auth-text-subtle)",
        }}
      >
        <span style={{ color: value ? STRENGTH_COLORS[s.score] : "var(--auth-text-subtle)" }}>
          {s.label || " "}
        </span>
        <span>
          {s.tips.length ? `Add: ${s.tips.join(", ")}` : value ? "Looking good" : " "}
        </span>
      </div>
    </div>
  );
}
